import React from 'react';
import { toast } from 'react-toastify';
import authService, { type Store as StoreType } from '../../../../services/authService';
import type { ManualLottoEntryFormValues, ManualPayoutEntryFormValues } from '../../../../types/features/storeDetails/manualEntries';

type UseStoreDetailsManualEntriesParams = {
    store: StoreType | null;
    todayDate: string;
};

const INITIAL_MANUAL_LOTTO_PAYLOAD: ManualLottoEntryFormValues = {
    business_date: '',
    lotto_sales_amount: '',
    lotto_sales_qty: '',
};

const INITIAL_MANUAL_PAYOUT_PAYLOAD: ManualPayoutEntryFormValues = {
    business_date: '',
    payout_amount: '',
};

export const useStoreDetailsManualEntries = ({ store, todayDate }: UseStoreDetailsManualEntriesParams) => {
    const [manualLottoSubmitting, setManualLottoSubmitting] = React.useState(false);
    const [manualPayoutSubmitting, setManualPayoutSubmitting] = React.useState(false);
    const [manualLottoPayload, setManualLottoPayload] = React.useState<ManualLottoEntryFormValues>(INITIAL_MANUAL_LOTTO_PAYLOAD);
    const [manualPayoutPayload, setManualPayoutPayload] = React.useState<ManualPayoutEntryFormValues>(INITIAL_MANUAL_PAYOUT_PAYLOAD);

    const canShowManualLottoSales = store?.has_vending_machine === true;
    const canShowManualPayout = store?.enable_manual_payout_entry === true;
    const hasManualEntryOptions = canShowManualLottoSales || canShowManualPayout;

    const isManualLottoFormValid = React.useMemo(
        () =>
            Boolean(manualLottoPayload.business_date) &&
            manualLottoPayload.business_date <= todayDate &&
            Number(manualLottoPayload.lotto_sales_amount) > 0,
        [manualLottoPayload.business_date, manualLottoPayload.lotto_sales_amount, todayDate],
    );

    const isManualPayoutFormValid = React.useMemo(
        () =>
            Boolean(manualPayoutPayload.business_date) &&
            manualPayoutPayload.business_date <= todayDate &&
            Number(manualPayoutPayload.payout_amount) > 0,
        [manualPayoutPayload.business_date, manualPayoutPayload.payout_amount, todayDate],
    );

    const handleSubmitManualLottoSales = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store is required.');
            return;
        }
        if (!manualLottoPayload.business_date) {
            toast.error('Business date is required.');
            return;
        }
        if (manualLottoPayload.business_date > todayDate) {
            toast.error('Business date cannot be later than today.');
            return;
        }
        if (!(Number(manualLottoPayload.lotto_sales_amount) > 0)) {
            toast.error('Lotto sales amount must be greater than 0.');
            return;
        }

        try {
            setManualLottoSubmitting(true);
            await authService.submitManualLottoSalesEntry(store.id, {
                business_date: manualLottoPayload.business_date,
                lotto_sales_amount: Number(manualLottoPayload.lotto_sales_amount),
                lotto_sales_qty: manualLottoPayload.lotto_sales_qty.trim() ? Number(manualLottoPayload.lotto_sales_qty) : 0,
            });
            toast.success('Manual lotto-sales entry submitted successfully.');
            setManualLottoPayload(INITIAL_MANUAL_LOTTO_PAYLOAD);
        } catch (error) {
            console.error('Failed to submit manual lotto-sales entry', error);
            toast.error('Failed to submit manual lotto-sales entry. Please try again.');
        } finally {
            setManualLottoSubmitting(false);
        }
    }, [manualLottoPayload, store?.id, todayDate]);

    const handleSubmitManualPayout = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store is required.');
            return;
        }
        if (!manualPayoutPayload.business_date) {
            toast.error('Business date is required.');
            return;
        }
        if (manualPayoutPayload.business_date > todayDate) {
            toast.error('Business date cannot be later than today.');
            return;
        }
        if (!(Number(manualPayoutPayload.payout_amount) > 0)) {
            toast.error('Payout amount must be greater than 0.');
            return;
        }

        try {
            setManualPayoutSubmitting(true);
            await authService.submitManualPayoutEntry(store.id, {
                business_date: manualPayoutPayload.business_date,
                payout_amount: Number(manualPayoutPayload.payout_amount),
            });
            toast.success('Manual payout entry submitted successfully.');
            setManualPayoutPayload(INITIAL_MANUAL_PAYOUT_PAYLOAD);
        } catch (error) {
            console.error('Failed to submit manual payout entry', error);
            toast.error('Failed to submit manual payout entry. Please try again.');
        } finally {
            setManualPayoutSubmitting(false);
        }
    }, [manualPayoutPayload, store?.id, todayDate]);

    return {
        canShowManualLottoSales,
        canShowManualPayout,
        hasManualEntryOptions,
        handleSubmitManualLottoSales,
        handleSubmitManualPayout,
        isManualLottoFormValid,
        isManualPayoutFormValid,
        manualLottoPayload,
        manualLottoSubmitting,
        manualPayoutPayload,
        manualPayoutSubmitting,
        setManualLottoPayload,
        setManualPayoutPayload,
    };
};
